import '@/styles/globals.css';
import '@/lib/broadcast-channel-polyfill';
import 'react-loading-skeleton/dist/skeleton.css';
import { HackleProvider } from '@hackler/react-sdk';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAccessCookie } from '@zelos/shared/lib/access-cookie';
import { enableMapSet } from 'immer';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useMemo } from 'react';
import Layout from '@/components/layout';
import { useResizeObserveBody } from '@/lib/breakpoint.service';
import { hackleClient } from '@/lib/hackle.service';
import { useAwaitInitMsw } from '@/lib/use-await-init-msw';
import { useProgressRouteChange } from '@/lib/use-progress-route-change';
import shareImage from '@/public/images/share-image.png';

enableMapSet();

const MyApp = ({
  Component,
  pageProps,
  router,
}: AppProps<MyAppPageProps> & { Component: NextPageWithAppProps }) => {
  useAccessCookie();
  useResizeObserveBody();

  const ProgressRouteChange = useProgressRouteChange();

  const layoutProps = useMemo(
    () => Component.getLayoutProps?.(),
    [Component],
  );

  const pagePropsByGetPageProps = useMemo(
    () => Component.getPageProps?.() ?? {},
    [Component],
  );

  const {
    pageDescription,
    pageShareImage,
    pageTitle,
    pageTitleDelimiter,
    ...pageComponentProps
  } = useMemo(() => Object.assign(pagePropsByGetPageProps, pageProps), [pagePropsByGetPageProps, pageProps]);

  const initMswResolved = useAwaitInitMsw();
  if (!initMswResolved) return null;

  const title = [pageTitle, '프로그래머스인증시험']
    .filter(Boolean)
    .join(pageTitleDelimiter || ' | ');
  const description = pageDescription || '코딩 역량에 대한 객관적 측정을 위해 체계적으로 설계된 프로그래머스 인증시험';

  const base = process.env.HOST;
  const ogUrl = new URL(router.asPath, base);
  const image = new URL(pageShareImage?.src || shareImage.src, base);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta
          content={description}
          name="description"
        />
        <meta
          content={title}
          property="og:title"
          key="og:title"
        />
        <meta
          content={description}
          property="og:description"
          key="og:description"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          content={ogUrl.href}
          property="og:url"
          key="og:url"
        />
        <meta
          content={image.href}
          property="og:image"
          key="og:image"
        />
        <meta
          content="website"
          property="og:type"
        />
        <meta
          content="summary_large_image"
          name="twitter:card"
          key="twitter:card"
        />
        <meta
          content={title}
          name="twitter:title"
          key="twitter:title"
        />
        <meta
          content={description}
          name="twitter:description"
          key="twitter:description"
        />
        <meta
          content={image.href}
          name="twitter:image"
          key="twitter:image"
        />
      </Head>
      <HackleProvider hackleClient={hackleClient} supportSSR>
        <QueryClientProvider client={new QueryClient()}>
          <Layout {...layoutProps}>
            {ProgressRouteChange}
            <Component {...pageComponentProps} />
          </Layout>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </HackleProvider>
    </>
  );
};

export default MyApp;
